import { MembershipType } from "@app/exports/enumerations";
import { FC, useCallback, useContext, useEffect, useState, useMemo } from "react";
import { LocationConfidenceScoresLabelEnum, NetworkIntegrityAggregateKey, ProviderActionStatusEnum, RegionSetStatusType } from "@app/projects/enumerators";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import DualRangeSlider from "@app/shared/DualRangeSlider";
import { FlyoutContext, FlyoutFooter } from "@app/shared/Flyouts";
import RoundCheckbox from "@app/shared/RoundCheckbox";
import { ClaimsActivityVolumeLabelEnum } from "@app/projects/network-assessment/pci-dashboard/claims-activity";
import { EffectivenessEfficiencyValueEnum, MotiveScoreValueEnum } from "@app/projects/servicing-provider";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { ProviderActionStatusEnumToDisplay } from "@app/projects/utility";
import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import { ProviderHubContext } from "@app/projects/network-assessment/provider-hub/contexts/ProviderHubContext";
import { ProviderSearchByEnum, ProviderViewByEnum } from "@app/projects/network-assessment/provider-hub/interfaces";
import MultiselectDropdown from "@app/shared/checkbox-dropdown/MultiselectDropdown";
import MultiselectNestedDropdown from "@app/shared/checkbox-dropdown/MultiselectNestedDropdown";
import { DropdownItem } from "@app/shared/checkbox-dropdown/types";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { getSelectionText } from "@app/shared/utility/getSelectionText";
import { getCareJourneyDefinitions } from "@app/projects/servicing-provider/ServicingProviderHelpers";
import Collapsible from "@app/shared/Collapsible";
import InfoIndicator from "@app/shared/InfoIndicator";
import TypeaheadMultiselectDropdown from "@app/projects/network-assessment/provider-hub/TypeaheadMultiselectDropdown";
import { niHideLabels as baseNiHideLabels } from "@app/projects/network-assessment/provider-hub/utility";

const MAX_TOP_PRESCRIPTIONS = 50;

const niProjectOptionsExclusionsValues = [
  NetworkIntegrityAggregateKey.DeactivatedNpi,
  NetworkIntegrityAggregateKey.InvalidNpi,
  NetworkIntegrityAggregateKey.MissingNpi,
  NetworkIntegrityAggregateKey.OigExclusion,
  NetworkIntegrityAggregateKey.MedicareOptOut,
  NetworkIntegrityAggregateKey.MedicarePrecluded,
];

const niAttestationBasedAlertsValues = [
  NetworkIntegrityAggregateKey.ClosedLocation,
  NetworkIntegrityAggregateKey.RetiredPractitioner,
  NetworkIntegrityAggregateKey.LeftOffice,
  NetworkIntegrityAggregateKey.NotRecognized,
  NetworkIntegrityAggregateKey.NoLongerOnRoster,
  NetworkIntegrityAggregateKey.TermedOnRoster,
];


const allChecked = (values: string[], selected: string[]) =>
  values.length > 0 && values.every(v => selected.includes(v))

const someChecked = (values: string[], selected: string[]) =>
  values.some(v => selected.includes(v))

const ProviderHubFilters: FC = () => {

  const providerHubContext = useContext(ProviderHubContext);
  const flyoutContext = useContext(FlyoutContext);
  const ffContext = useContext(FeatureFlagContext);
  const veryLowOnly = ffContext.enabledFeatureFlags.has(Features.NETWORK_INTEGRITY_VERY_LOW_LOCATION_CONFIDENCE_ONLY);

  // Local state variables, used to temporarily hold the values until the user clicks the Apply button,
  // which would forward the values to the corresponding context state/reducer variables
  const [selectedProviderNetworkTypes, setSelectedProviderNetworkTypes] = useState<ProviderNetworkTypeEnum[]>(providerHubContext.filterState.providerNetworkTypes);
  const [providerNetworkTypeSelectionText, setProviderNetworkTypeSelectionText] = useState<string>("");
  const [selectedProviderActionStatuses, setSelectedProviderActionStatuses] = useState<(ProviderActionStatusEnum | null)[]>(providerHubContext.filterState.providerActionStatuses);
  const [statusSelectionText, setStatusSelectionText] = useState<string>("");
  const [selectedCloseGapsMin, setSelectedCloseGapsMin] = useState<number>(providerHubContext.filterState.closeGapsMin);
  const [selectedCloseGapsMax, setSelectedCloseGapsMax] = useState<number>(providerHubContext.filterState.closeGapsMax);
  const [selectedAccessPointCountMin, setSelectedAccessPointCountMin] = useState<number>(providerHubContext.filterState.accessPointCountMin);
  const [selectedAccessPointCountMax, setSelectedAccessPointCountMax] = useState<number>(providerHubContext.filterState.accessPointCountMax);
  const [selectedEffectiveness, setSelectedEffectiveness] = useState<number[]>(providerHubContext.filterState.effectiveness);
  const [selectedEfficiency, setSelectedEfficiency] = useState<number[]>(providerHubContext.filterState.efficiency);
  const [selectedMotiveScore, setSelectedMotiveScore] = useState<number[]>(providerHubContext.filterState.motiveScore);
  const [selectedTotalClaimsVolume, setSelectedTotalClaimsVolume] = useState<DropdownItem<string>[]>(providerHubContext.filterState.totalClaimsVolume);
  const [totalClaimsVolumeSelectionText, setTotalClaimsVolumeSelectionText] = useState<string>("");
  const [selectedTotalNewPatientIndicator, setSelectedTotalNewPatientIndicator] = useState<DropdownItem<boolean | null>[]>(providerHubContext.filterState.totalNewPatientIndicator);
  const [totalNewPatientIndicatorSelectionText, setTotalNewPatientIndicatorSelectionText] = useState<string>("");
  const [selectedCommercialClaimsVolume, setSelectedCommercialClaimsVolume] = useState<DropdownItem<string>[]>(providerHubContext.filterState.commercialClaimsVolume);
  const [commercialClaimsVolumeSelectionText, setCommercialClaimsVolumeSelectionText] = useState<string>("");
  const [selectedCommercialNewPatientIndicator, setSelectedCommercialNewPatientIndicator] = useState<DropdownItem<boolean | null>[]>(providerHubContext.filterState.commercialNewPatientIndicator);
  const [commercialNewPatientIndicatorSelectionText, setCommercialNewPatientIndicatorSelectionText] = useState<string>("");
  const [selectedMedicaidClaimsVolume, setSelectedMedicaidClaimsVolume] = useState<DropdownItem<string>[]>(providerHubContext.filterState.medicaidClaimsVolume);
  const [medicaidClaimsVolumeSelectionText, setMedicaidClaimsVolumeSelectionText] = useState<string>("");
  const [selectedMedicaidNewPatientIndicator, setSelectedMedicaidNewPatientIndicator] = useState<DropdownItem<boolean | null>[]>(providerHubContext.filterState.medicaidNewPatientIndicator);
  const [medicaidNewPatientIndicatorSelectionText, setMedicaidNewPatientIndicatorSelectionText] = useState<string>("");
  const [selectedMedicareClaimsVolume, setSelectedMedicareClaimsVolume] = useState<DropdownItem<string>[]>(providerHubContext.filterState.medicareClaimsVolume);
  const [medicareClaimsVolumeSelectionText, setMedicareClaimsVolumeSelectionText] = useState<string>("");
  const [selectedMedicareNewPatientIndicator, setSelectedMedicareNewPatientIndicator] = useState<DropdownItem<boolean | null>[]>(providerHubContext.filterState.medicareNewPatientIndicator);
  const [medicareNewPatientIndicatorSelectionText, setMedicareNewPatientIndicatorSelectionText] = useState<string>("");
  const [selectedTricareClaimsVolume, setSelectedTricareClaimsVolume] = useState<DropdownItem<string>[]>(providerHubContext.filterState.tricareClaimsVolume);
  const [tricareClaimsVolumeSelectionText, setTricareClaimsVolumeSelectionText] = useState<string>("");
  const [selectedTricareNewPatientIndicator, setSelectedTricareNewPatientIndicator] = useState<DropdownItem<boolean | null>[]>(providerHubContext.filterState.tricareNewPatientIndicator);
  const [tricareNewPatientIndicatorSelectionText, setTricareNewPatientIndicatorSelectionText] = useState<string>("");
  const [selectedVaClaimsVolume, setSelectedVaClaimsVolume] = useState<DropdownItem<string>[]>(providerHubContext.filterState.vaClaimsVolume);
  const [vaClaimsVolumeSelectionText, setVaClaimsVolumeSelectionText] = useState<string>("");
  const [selectedVaNewPatientIndicator, setSelectedVaNewPatientIndicator] = useState<DropdownItem<boolean | null>[]>(providerHubContext.filterState.vaNewPatientIndicator);
  const [vaNewPatientIndicatorSelectionText, setVaNewPatientIndicatorSelectionText] = useState<string>("");
  const [selectedTopProcedures, setSelectedTopProcedures] = useState<DropdownItem<string>[]>(providerHubContext.filterState.topProcedures);
  const [topProceduresSelectionText, setTopProceduresSelectionText] = useState<string>("");
  const [selectedTopConditions, setSelectedTopConditions] = useState<DropdownItem<string>[]>(providerHubContext.filterState.topConditions);
  const [topConditionsSelectionText, setTopConditionsSelectionText] = useState<string>("");
  const [selectedTopPrescriptions, setSelectedTopPrescriptions] = useState<DropdownItem<string>[]>(providerHubContext.filterState.topPrescriptions);
  const [topPrescriptionsSelectionText, setTopPrescriptionsSelectionText] = useState<string>("");
  const [selectedLocationConfidence, setSelectedLocationConfidence] = useState<DropdownItem<string>[]>(providerHubContext.filterState.locationConfidence);
  const [locationConfidenceSelectionText, setLocationConfidenceSelectionText] = useState<string>("");
  const [selectedMotiveScoreMin, setSelectedMotiveScoreMin] = useState<number>(providerHubContext.filterState.motiveScoreMin);
  const [selectedMotiveScoreMax, setSelectedMotiveScoreMax] = useState<number>(providerHubContext.filterState.motiveScoreMax);
  const [selectedServicingProviderCountMin, setSelectedServicingProviderCountMin] = useState<number>(providerHubContext.filterState.servicingProviderCountMin);
  const [selectedServicingProviderCountMax, setSelectedServicingProviderCountMax] = useState<number>(providerHubContext.filterState.servicingProviderCountMax);
  const [selectedCareJourneyCostScore, setSelectedCareJourneyCostScore] = useState<number[]>(providerHubContext.filterState.careJourneyCostScore ?? []);
  const [selectedCareJourneyQualityScore, setSelectedCareJourneyQualityScore] = useState<number[]>(providerHubContext.filterState.careJourneyQualityScore ?? []);
  const [selectedCareJourneyCostScoreMin, setSelectedCareJourneyCostScoreMin] = useState<number>(providerHubContext.filterState.careJourneyCostScoreMin);
  const [selectedCareJourneyCostScoreMax, setSelectedCareJourneyCostScoreMax] = useState<number>(providerHubContext.filterState.careJourneyCostScoreMax);
  const [selectedCareJourneyQualityScoreMin, setSelectedCareJourneyQualityScoreMin] = useState<number>(providerHubContext.filterState.careJourneyQualityScoreMin);
  const [selectedCareJourneyQualityScoreMax, setSelectedCareJourneyQualityScoreMax] = useState<number>(providerHubContext.filterState.careJourneyQualityScoreMax);
  const [selectedEffectivenessMin, setSelectedEffectivenessMin] = useState<number>(providerHubContext.filterState.effectivenessMin);
  const [selectedEffectivenessMax, setSelectedEffectivenessMax] = useState<number>(providerHubContext.filterState.effectivenessMax);
  const [selectedEfficiencyMin, setSelectedEfficiencyMin] = useState<number>(providerHubContext.filterState.efficiencyMin);
  const [selectedEfficiencyMax, setSelectedEfficiencyMax] = useState<number>(providerHubContext.filterState.efficiencyMax);
  const [includeUnknownEffectiveness, setIncludeUnknownEffectiveness] = useState<boolean>(providerHubContext.filterState.includeUnknownEffectiveness ?? false);
  const [includeUnknownEfficiency, setIncludeUnknownEfficiency] = useState<boolean>(providerHubContext.filterState.includeUnknownEfficiency ?? false);
  const [includeUnknownMotiveScore, setIncludeUnknownMotiveScore] = useState<boolean>(providerHubContext.filterState.includeUnknownMotiveScore ?? false);
  const [includeUnknownCareJourneyCostScore, setIncludeUnknownCareJourneyCostScore] = useState<boolean>(providerHubContext.filterState.includeUnknownCareJourneyCostScore ?? false);
  const [includeUnknownCareJourneyQualityScore, setIncludeUnknownCareJourneyQualityScore] = useState<boolean>(providerHubContext.filterState.includeUnknownCareJourneyQualityScore ?? false);
  const isAggregateView = providerHubContext.filterState.searchByOption === ProviderSearchByEnum.ContractEntity
    || providerHubContext.filterState.viewByOption === ProviderViewByEnum.Organization
    || providerHubContext.filterState.viewByOption === ProviderViewByEnum.Hospital;
  const [selectedNiProjectOptionsExclusions, setSelectedNiProjectOptionsExclusions] = useState<string[]>(providerHubContext.filterState.networkIntegrityProjectOptionsExclusions ?? []);
  const [selectedNiCriticalAlerts, setSelectedNiCriticalAlerts] = useState<string[]>(providerHubContext.filterState.networkIntegrityCriticalAlerts ?? []);
  const [selectedNiAttestationBasedAlerts, setSelectedNiAttestationBasedAlerts] = useState<string[]>(providerHubContext.filterState.networkIntegrityAttestationBasedAlerts ?? []);
  const [selectedExcessAddressesMin, setSelectedExcessAddressesMin] = useState<number>(providerHubContext.filterState.networkIntegrityExcessAddressesMin);
  const [selectedExcessAddressesMax, setSelectedExcessAddressesMax] = useState<number>(providerHubContext.filterState.networkIntegrityExcessAddressesMax);
  const [selectedExcessSpecialtiesMin, setSelectedExcessSpecialtiesMin] = useState<number>(providerHubContext.filterState.networkIntegrityExcessSpecialtiesMin);
  const [selectedExcessSpecialtiesMax, setSelectedExcessSpecialtiesMax] = useState<number>(providerHubContext.filterState.networkIntegrityExcessSpecialtiesMax);

  const niCriticalAlertsValues = [
    NetworkIntegrityAggregateKey.ExpiredLicensePractitioners,
    ...(providerHubContext.networkIntegrityGhostProviderLobLabel ? [providerHubContext.networkIntegrityGhostProviderLobLabel] : []),
    NetworkIntegrityAggregateKey.GhostProviders,
    veryLowOnly ? NetworkIntegrityAggregateKey.VeryLowLocationConfidence : NetworkIntegrityAggregateKey.LowLocationConfidence,
  ];

  const niHideLabels: Record<string, string> = useMemo(() => ({
    ...baseNiHideLabels,
    ...(providerHubContext.networkIntegrityGhostProviderLobLabel && {
      [providerHubContext.networkIntegrityGhostProviderLobLabel]: `Hide ${providerHubContext.networkIntegrityGhostProviderLobLabel}`,
    }),
  }), [providerHubContext.networkIntegrityGhostProviderLobLabel]);

  const handleCancel = () => {
    setSelectedProviderNetworkTypes([]);
    setSelectedProviderActionStatuses([]);
    setSelectedCloseGapsMin(providerHubContext.defaultCloseGapsRangeMin);
    setSelectedCloseGapsMax(providerHubContext.defaultCloseGapsRangeMax);
    setSelectedAccessPointCountMin(providerHubContext.defaultAccessPointsRangeMin);
    setSelectedAccessPointCountMax(providerHubContext.defaultAccessPointsRangeMax);
    setSelectedEffectiveness([]);
    setSelectedEfficiency([]);
    setSelectedMotiveScore([]);
    setSelectedTotalClaimsVolume([]);
    setSelectedTotalNewPatientIndicator([]);
    setSelectedCommercialClaimsVolume([]);
    setSelectedCommercialNewPatientIndicator([]);
    setSelectedMedicaidClaimsVolume([]);
    setSelectedMedicaidNewPatientIndicator([]);
    setSelectedMedicareClaimsVolume([]);
    setSelectedMedicareNewPatientIndicator([]);
    setSelectedTricareClaimsVolume([]);
    setSelectedTricareNewPatientIndicator([]);
    setSelectedVaClaimsVolume([]);
    setSelectedVaNewPatientIndicator([]);
    setSelectedTopProcedures([]);
    setSelectedTopConditions([]);
    setSelectedTopPrescriptions([]);
    setSelectedLocationConfidence([]);
    setSelectedCareJourneyCostScore([]);
    setSelectedCareJourneyQualityScore([]);
    setSelectedMotiveScoreMin(providerHubContext.defaultMotiveScoreRangeMin);
    setSelectedMotiveScoreMax(providerHubContext.defaultMotiveScoreRangeMax);
    setSelectedServicingProviderCountMin(providerHubContext.defaultServicingProviderCountMin);
    setSelectedServicingProviderCountMax(providerHubContext.defaultServicingProviderCountMax);
    setSelectedCareJourneyCostScoreMin(providerHubContext.defaultCareJourneyCostRangeMin);
    setSelectedCareJourneyCostScoreMax(providerHubContext.defaultCareJourneyCostRangeMax);
    setSelectedCareJourneyQualityScoreMin(providerHubContext.defaultCareJourneyQualityRangeMin);
    setSelectedCareJourneyQualityScoreMax(providerHubContext.defaultCareJourneyQualityRangeMax);
    setSelectedEffectivenessMin(providerHubContext.defaultEffectivenessRangeMin);
    setSelectedEffectivenessMax(providerHubContext.defaultEffectivenessRangeMax);
    setSelectedEfficiencyMin(providerHubContext.defaultEfficiencyRangeMin);
    setSelectedEfficiencyMax(providerHubContext.defaultEfficiencyRangeMax);
    setIncludeUnknownEffectiveness(true);
    setIncludeUnknownEfficiency(true);
    setIncludeUnknownMotiveScore(true);
    setIncludeUnknownCareJourneyCostScore(true);
    setIncludeUnknownCareJourneyQualityScore(true);
    setSelectedNiProjectOptionsExclusions([]);
    setSelectedNiCriticalAlerts([]);
    setSelectedNiAttestationBasedAlerts([]);
    setSelectedExcessAddressesMin(providerHubContext.defaultExcessAddressesRangeMin);
    setSelectedExcessAddressesMax(providerHubContext.defaultExcessAddressesRangeMax);
    setSelectedExcessSpecialtiesMin(providerHubContext.defaultExcessSpecialtiesRangeMin);
    setSelectedExcessSpecialtiesMax(providerHubContext.defaultExcessSpecialtiesRangeMax);
  };

  const handleApply = async () => {
    providerHubContext.setFilter('providerNetworkTypes', selectedProviderNetworkTypes);
    providerHubContext.setFilter('providerActionStatuses', selectedProviderActionStatuses);
    providerHubContext.setFilter('closeGapsMin', selectedCloseGapsMin);
    providerHubContext.setFilter('closeGapsMax', selectedCloseGapsMax);
    providerHubContext.setFilter('accessPointCountMin', selectedAccessPointCountMin);
    providerHubContext.setFilter('accessPointCountMax', selectedAccessPointCountMax);
    providerHubContext.setFilter('effectiveness', selectedEffectiveness);
    providerHubContext.setFilter('efficiency', selectedEfficiency);
    providerHubContext.setFilter('motiveScore', selectedMotiveScore);
    providerHubContext.setFilter('totalClaimsVolume', selectedTotalClaimsVolume);
    providerHubContext.setFilter('totalNewPatientIndicator', selectedTotalNewPatientIndicator);
    providerHubContext.setFilter('commercialClaimsVolume', selectedCommercialClaimsVolume);
    providerHubContext.setFilter('commercialNewPatientIndicator', selectedCommercialNewPatientIndicator);
    providerHubContext.setFilter('medicaidClaimsVolume', selectedMedicaidClaimsVolume);
    providerHubContext.setFilter('medicaidNewPatientIndicator', selectedMedicaidNewPatientIndicator);
    providerHubContext.setFilter('medicareClaimsVolume', selectedMedicareClaimsVolume);
    providerHubContext.setFilter('medicareNewPatientIndicator', selectedMedicareNewPatientIndicator);
    providerHubContext.setFilter('tricareClaimsVolume', selectedTricareClaimsVolume);
    providerHubContext.setFilter('tricareNewPatientIndicator', selectedTricareNewPatientIndicator);
    providerHubContext.setFilter('vaClaimsVolume', selectedVaClaimsVolume);
    providerHubContext.setFilter('vaNewPatientIndicator', selectedVaNewPatientIndicator);
    providerHubContext.setFilter('topProcedures', selectedTopProcedures);
    providerHubContext.setFilter('topConditions', selectedTopConditions);
    providerHubContext.setFilter('topPrescriptions', selectedTopPrescriptions);
    providerHubContext.setFilter('locationConfidence', selectedLocationConfidence);
    providerHubContext.setFilter('careJourneyCostScore', selectedCareJourneyCostScore);
    providerHubContext.setFilter('careJourneyQualityScore', selectedCareJourneyQualityScore);
    providerHubContext.setFilter('careJourneyCostScoreMin', selectedCareJourneyCostScoreMin);
    providerHubContext.setFilter('careJourneyCostScoreMax', selectedCareJourneyCostScoreMax);
    providerHubContext.setFilter('careJourneyQualityScoreMin', selectedCareJourneyQualityScoreMin);
    providerHubContext.setFilter('careJourneyQualityScoreMax', selectedCareJourneyQualityScoreMax);
    providerHubContext.setFilter('motiveScoreMin', selectedMotiveScoreMin);
    providerHubContext.setFilter('motiveScoreMax', selectedMotiveScoreMax);
    providerHubContext.setFilter('effectivenessMin', selectedEffectivenessMin);
    providerHubContext.setFilter('effectivenessMax', selectedEffectivenessMax);
    providerHubContext.setFilter('efficiencyMin', selectedEfficiencyMin);
    providerHubContext.setFilter('efficiencyMax', selectedEfficiencyMax);
    providerHubContext.setFilter('includeUnknownEffectiveness', includeUnknownEffectiveness);
    providerHubContext.setFilter('includeUnknownEfficiency', includeUnknownEfficiency);
    providerHubContext.setFilter('includeUnknownMotiveScore', includeUnknownMotiveScore);
    providerHubContext.setFilter('includeUnknownCareJourneyCostScore', includeUnknownCareJourneyCostScore);
    providerHubContext.setFilter('includeUnknownCareJourneyQualityScore', includeUnknownCareJourneyQualityScore);
    providerHubContext.setFilter('servicingProviderCountMin', selectedServicingProviderCountMin);
    providerHubContext.setFilter('servicingProviderCountMax', selectedServicingProviderCountMax);
    providerHubContext.setFilter('networkIntegrityProjectOptionsExclusions', selectedNiProjectOptionsExclusions);
    providerHubContext.setFilter('networkIntegrityCriticalAlerts', selectedNiCriticalAlerts);
    providerHubContext.setFilter('networkIntegrityAttestationBasedAlerts', selectedNiAttestationBasedAlerts);
    providerHubContext.setFilter('networkIntegrityExcessAddressesMin', selectedExcessAddressesMin);
    providerHubContext.setFilter('networkIntegrityExcessAddressesMax', selectedExcessAddressesMax);
    providerHubContext.setFilter('networkIntegrityExcessSpecialtiesMin', selectedExcessSpecialtiesMin);
    providerHubContext.setFilter('networkIntegrityExcessSpecialtiesMax', selectedExcessSpecialtiesMax);
    flyoutContext.handleCloseFlyout().then();
  };

  const getEffectivenessChecked = useCallback((value: number): boolean => {
    return selectedEffectiveness.includes(value);
  }, [selectedEffectiveness]);

  const getEfficiencyChecked = useCallback((value: number): boolean => {
    return selectedEfficiency.includes(value);
  }, [selectedEfficiency]);

  const getMotiveScoreChecked = useCallback((value: number): boolean => {
    return selectedMotiveScore.includes(value);
  }, [selectedMotiveScore]);

  const handleEffectivenessClick = useCallback((value: number) => {
    setSelectedEffectiveness(prevEffectiveness =>
      prevEffectiveness.includes(value)
        ? prevEffectiveness.filter(s => s !== value)
        : [...prevEffectiveness, value]
    );
  }, [])

  const handleEfficiencyClick = useCallback((value: number) => {
    setSelectedEfficiency(prevEfficiency =>
      prevEfficiency.includes(value)
        ? prevEfficiency.filter(s => s !== value)
        : [...prevEfficiency, value]
    );
  }, [])

  const handleMotiveScoreClick = useCallback((value: number) => {
    setSelectedMotiveScore(prevMotiveScore =>
      prevMotiveScore.includes(value)
        ? prevMotiveScore.filter(s => s !== value)
        : [...prevMotiveScore, value]
    );
  }, [])

  const getCareJourneyCostScoreChecked = useCallback((value: number): boolean => {
    return selectedCareJourneyCostScore.includes(value);
  }, [selectedCareJourneyCostScore]);

  const getCareJourneyQualityScoreChecked = useCallback((value: number): boolean => {
    return selectedCareJourneyQualityScore.includes(value);
  }, [selectedCareJourneyQualityScore]);

  const handleCareJourneyCostScoreClick = useCallback((value: number) => {
    setSelectedCareJourneyCostScore(prev =>
      prev.includes(value)
        ? prev.filter(s => s !== value)
        : [...prev, value]
    );
  }, [])

  const handleCareJourneyQualityScoreClick = useCallback((value: number) => {
    setSelectedCareJourneyQualityScore(prev =>
      prev.includes(value)
        ? prev.filter(s => s !== value)
        : [...prev, value]
    );
  }, [])

  useEffect(() => {
    if (selectedProviderNetworkTypes.length === 0) {
      setProviderNetworkTypeSelectionText(providerHubContext.allOptionsText);
    } else {
      const totalProviderNetworkTypeOptions = providerHubContext.providerNetworkTypeOptions.flatMap(parent =>
        parent.children.length > 0 ? parent.children.map(child => child.name) : [parent.name]
      );
      setProviderNetworkTypeSelectionText(getSelectionText(selectedProviderNetworkTypes, totalProviderNetworkTypeOptions));
    }
  }, [selectedProviderNetworkTypes, providerHubContext.providerNetworkTypeOptions]);

  useEffect(() => {
    selectedProviderActionStatuses.length === 0
      ? setStatusSelectionText(providerHubContext.allOptionsText)
      : setStatusSelectionText(getSelectionText(selectedProviderActionStatuses, providerHubContext.providerActionStatusOptions));
  }, [selectedProviderActionStatuses, providerHubContext.providerActionStatusOptions]);

  useEffect(() => {
    selectedTotalClaimsVolume.length === 0 ?
      setTotalClaimsVolumeSelectionText(providerHubContext.allOptionsText) :
      setTotalClaimsVolumeSelectionText(getSelectionText(selectedTotalClaimsVolume, providerHubContext.pciVolumeOptions));
  }, [selectedTotalClaimsVolume, providerHubContext.pciVolumeOptions]);

  useEffect(() => {
    selectedTotalNewPatientIndicator.length === 0 ?
      setTotalNewPatientIndicatorSelectionText(providerHubContext.allOptionsText) :
      setTotalNewPatientIndicatorSelectionText(getSelectionText(selectedTotalNewPatientIndicator, providerHubContext.pciClaimsOptions));
  }, [selectedTotalNewPatientIndicator, providerHubContext.pciClaimsOptions]);

  useEffect(() => {
    selectedCommercialClaimsVolume.length === 0 ?
      setCommercialClaimsVolumeSelectionText(providerHubContext.allOptionsText) :
      setCommercialClaimsVolumeSelectionText(getSelectionText(selectedCommercialClaimsVolume, providerHubContext.pciVolumeOptions));
  }, [selectedCommercialClaimsVolume, providerHubContext.pciVolumeOptions]);

  useEffect(() => {
    selectedCommercialNewPatientIndicator.length === 0 ?
      setCommercialNewPatientIndicatorSelectionText(providerHubContext.allOptionsText) :
      setCommercialNewPatientIndicatorSelectionText(getSelectionText(selectedCommercialNewPatientIndicator, providerHubContext.pciClaimsOptions));
  }, [selectedCommercialNewPatientIndicator, providerHubContext.pciClaimsOptions]);

  useEffect(() => {
    selectedMedicaidClaimsVolume.length === 0 ?
      setMedicaidClaimsVolumeSelectionText(providerHubContext.allOptionsText) :
      setMedicaidClaimsVolumeSelectionText(getSelectionText(selectedMedicaidClaimsVolume, providerHubContext.pciVolumeOptions));
  }, [selectedMedicaidClaimsVolume, providerHubContext.pciVolumeOptions]);

  useEffect(() => {
    selectedMedicaidNewPatientIndicator.length === 0 ?
      setMedicaidNewPatientIndicatorSelectionText(providerHubContext.allOptionsText) :
      setMedicaidNewPatientIndicatorSelectionText(getSelectionText(selectedMedicaidNewPatientIndicator, providerHubContext.pciClaimsOptions));
  }, [selectedMedicaidNewPatientIndicator, providerHubContext.pciClaimsOptions]);

  useEffect(() => {
    selectedMedicareClaimsVolume.length === 0 ?
      setMedicareClaimsVolumeSelectionText(providerHubContext.allOptionsText) :
      setMedicareClaimsVolumeSelectionText(getSelectionText(selectedMedicareClaimsVolume, providerHubContext.pciVolumeOptions));
  }, [selectedMedicareClaimsVolume, providerHubContext.pciVolumeOptions]);

  useEffect(() => {
    selectedMedicareNewPatientIndicator.length === 0 ?
      setMedicareNewPatientIndicatorSelectionText(providerHubContext.allOptionsText) :
      setMedicareNewPatientIndicatorSelectionText(getSelectionText(selectedMedicareNewPatientIndicator, providerHubContext.pciClaimsOptions));
  }, [selectedMedicareNewPatientIndicator, providerHubContext.pciClaimsOptions]);

  useEffect(() => {
    selectedTricareClaimsVolume.length === 0 ?
      setTricareClaimsVolumeSelectionText(providerHubContext.allOptionsText) :
      setTricareClaimsVolumeSelectionText(getSelectionText(selectedTricareClaimsVolume, providerHubContext.pciVolumeOptions));
  }, [selectedTricareClaimsVolume, providerHubContext.pciVolumeOptions]);

  useEffect(() => {
    selectedTricareNewPatientIndicator.length === 0 ?
      setTricareNewPatientIndicatorSelectionText(providerHubContext.allOptionsText) :
      setTricareNewPatientIndicatorSelectionText(getSelectionText(selectedTricareNewPatientIndicator, providerHubContext.pciClaimsOptions));
  }, [selectedTricareNewPatientIndicator, providerHubContext.pciClaimsOptions]);

  useEffect(() => {
    selectedVaClaimsVolume.length === 0 ?
      setVaClaimsVolumeSelectionText(providerHubContext.allOptionsText) :
      setVaClaimsVolumeSelectionText(getSelectionText(selectedVaClaimsVolume, providerHubContext.pciVolumeOptions));
  }, [selectedVaClaimsVolume, providerHubContext.pciVolumeOptions]);

  useEffect(() => {
    selectedVaNewPatientIndicator.length === 0 ?
      setVaNewPatientIndicatorSelectionText(providerHubContext.allOptionsText) :
      setVaNewPatientIndicatorSelectionText(getSelectionText(selectedVaNewPatientIndicator, providerHubContext.pciClaimsOptions));
  }, [selectedVaNewPatientIndicator, providerHubContext.pciClaimsOptions]);

  useEffect(() => {
    selectedTopProcedures.length === 0 ?
      setTopProceduresSelectionText(providerHubContext.allOptionsText) :
      setTopProceduresSelectionText(getSelectionText(selectedTopProcedures, providerHubContext.topProcedureOptions));
  }, [selectedTopProcedures, providerHubContext.topProcedureOptions]);

  useEffect(() => {
    selectedTopConditions.length === 0 ?
      setTopConditionsSelectionText(providerHubContext.allOptionsText) :
      setTopConditionsSelectionText(getSelectionText(selectedTopConditions, providerHubContext.topConditionOptions));
  }, [selectedTopConditions, providerHubContext.topConditionOptions]);

  useEffect(() => {
    selectedTopPrescriptions.length === 0 ?
      setTopPrescriptionsSelectionText(providerHubContext.allOptionsText) :
      setTopPrescriptionsSelectionText(getSelectionText(selectedTopPrescriptions, providerHubContext.topPrescriptionOptions));
  }, [selectedTopPrescriptions, providerHubContext.topPrescriptionOptions]);

  useEffect(() => {
    selectedLocationConfidence.length === 0 ?
      setLocationConfidenceSelectionText(providerHubContext.allOptionsText) :
      setLocationConfidenceSelectionText(getSelectionText(selectedLocationConfidence, providerHubContext.locationConfidenceOptions));
  }, [selectedLocationConfidence, providerHubContext.locationConfidenceOptions]);

  useEffect(() => {
    setSelectedAccessPointCountMax(providerHubContext.filterState.accessPointCountMax);
  }, [providerHubContext.filterState.accessPointCountMax]);

  useEffect(() => {
    setSelectedServicingProviderCountMax(providerHubContext.filterState.servicingProviderCountMax);
  }, [providerHubContext.filterState.servicingProviderCountMax]);

  useEffect(() => {
    setSelectedCloseGapsMax(providerHubContext.filterState.closeGapsMax);
  }, [providerHubContext.filterState.closeGapsMax]);

  useEffect(() => {
    setSelectedMotiveScoreMin(providerHubContext.filterState.motiveScoreMin);
  }, [providerHubContext.filterState.motiveScoreMin]);

  useEffect(() => {
    setSelectedMotiveScoreMax(providerHubContext.filterState.motiveScoreMax);
  }, [providerHubContext.filterState.motiveScoreMax]);

  useEffect(() => {
    setSelectedCareJourneyCostScoreMin(providerHubContext.filterState.careJourneyCostScoreMin);
  }, [providerHubContext.filterState.careJourneyCostScoreMin]);

  useEffect(() => {
    setSelectedCareJourneyCostScoreMax(providerHubContext.filterState.careJourneyCostScoreMax);
  }, [providerHubContext.filterState.careJourneyCostScoreMax]);

  useEffect(() => {
    setSelectedCareJourneyQualityScoreMin(providerHubContext.filterState.careJourneyQualityScoreMin);
  }, [providerHubContext.filterState.careJourneyQualityScoreMin]);

  useEffect(() => {
    setSelectedCareJourneyQualityScoreMax(providerHubContext.filterState.careJourneyQualityScoreMax);
  }, [providerHubContext.filterState.careJourneyQualityScoreMax]);

  useEffect(() => {
    setSelectedEffectivenessMin(providerHubContext.filterState.effectivenessMin);
  }, [providerHubContext.filterState.effectivenessMin]);

  useEffect(() => {
    setSelectedEffectivenessMax(providerHubContext.filterState.effectivenessMax);
  }, [providerHubContext.filterState.effectivenessMax]);

  useEffect(() => {
    setSelectedEfficiencyMin(providerHubContext.filterState.efficiencyMin);
  }, [providerHubContext.filterState.efficiencyMin]);

  useEffect(() => {
    setSelectedEfficiencyMax(providerHubContext.filterState.efficiencyMax);
  }, [providerHubContext.filterState.efficiencyMax]);

  useEffect(() => {
    setIncludeUnknownEffectiveness(providerHubContext.filterState.includeUnknownEffectiveness ?? false);
  }, [providerHubContext.filterState.includeUnknownEffectiveness]);

  useEffect(() => {
    setIncludeUnknownEfficiency(providerHubContext.filterState.includeUnknownEfficiency ?? false);
  }, [providerHubContext.filterState.includeUnknownEfficiency]);

  useEffect(() => {
    setIncludeUnknownMotiveScore(providerHubContext.filterState.includeUnknownMotiveScore ?? false);
  }, [providerHubContext.filterState.includeUnknownMotiveScore]);

  useEffect(() => {
    setIncludeUnknownCareJourneyCostScore(providerHubContext.filterState.includeUnknownCareJourneyCostScore ?? false);
  }, [providerHubContext.filterState.includeUnknownCareJourneyCostScore]);

  useEffect(() => {
    setIncludeUnknownCareJourneyQualityScore(providerHubContext.filterState.includeUnknownCareJourneyQualityScore ?? false);
  }, [providerHubContext.filterState.includeUnknownCareJourneyQualityScore]);

  const effectivenessEfficiencyValues = Object.values(EffectivenessEfficiencyValueEnum)
    .map(v => Number(v))
    .filter(v => !isNaN(v)) as number[];

  const motiveScoreValues = Object.values(MotiveScoreValueEnum)
    .map(v => Number(v))
    .filter(v => !isNaN(v)) as number[];

  const topPrescriptionsErrorMessage = providerHubContext.errorLoadingTopPrescriptions ?
    "Failed to load Top Prescriptions, please refresh to try again." :
    selectedTopPrescriptions.length > MAX_TOP_PRESCRIPTIONS ?
      `A maximum of ${MAX_TOP_PRESCRIPTIONS.toLocaleString()} prescriptions can be selected at one time.` :
      undefined;

  const areFiltersValid = selectedTopPrescriptions.length <= MAX_TOP_PRESCRIPTIONS;

  return (
    <div className="provider-hub_advanced-filters-container">
      <p>Add filter criteria to refine your provider search results.</p>

      {providerHubContext.showMembershipTypeDropdown &&
        <QESSelect
          allowEmpty={false}
          className="filter-item"
          testId="provider-hub__filter-membership-type-single-select"
          label="Membership Type"
          onChange={(e) => providerHubContext.setFilter('membershipType', e.target.value as MembershipType)}
          value={providerHubContext.filterState.membershipType}>
          <option
            key={0}
            value={MembershipType.Primary}>
            {providerHubContext.membershipAliases?.primary}
          </option>
          <option
            key={1}
            value={MembershipType.Secondary}>
            {providerHubContext.membershipAliases?.secondary}
          </option>
        </QESSelect>
      }

      <MultiselectNestedDropdown
        containerClassName="filter-item"
        customSelectInputText={providerHubContext.serviceAreaSelectionText}
        filters={providerHubContext.serviceAreaStatusFilters}
        items={providerHubContext.serviceAreaOptions}
        label="Service Area"
        matchFilter={providerHubContext.serviceAreaMatchFilter}
        onClose={() => providerHubContext.setActiveServiceAreaStatusFilters(new Set())}
        onSelectedFiltersChange={(keys) =>
          providerHubContext.setActiveServiceAreaStatusFilters(new Set(keys as RegionSetStatusType[]))
        }
        onSelectionChanged={(selectedItems) => providerHubContext.setFilter('regionSetIds', selectedItems.map(Number))}
        selectedFilters={[...providerHubContext.activeServiceAreaStatusFilters]}
        selectedItems={providerHubContext.filterState.regionSetIds.map(String)}
        testId="provider-hub__filter-service-area-multi-select"
      />

      <MultiselectDropdown
        containerClassName="filter-item"
        customSelectInputText={providerHubContext.specialtySelectionText}
        items={providerHubContext.specialtyOptions ?? []}
        label="Specialty Group"
        onSelectionChanged={(selectedItems) => providerHubContext.setFilter('specialtyGroupCodes', selectedItems)}
        selectedItems={providerHubContext.filterState.specialtyGroupCodes}
        testId="provider-hub__filter-specialties-multi-select"
        showTooltipOnOverflow={true}
      />

      <div className="horizontal-divider" />

      <MultiselectNestedDropdown
        items={providerHubContext.providerNetworkTypeOptions}
        customSelectInputText={providerNetworkTypeSelectionText}
        containerClassName="filter-item"
        label="Provider Source"
        hideParentCheckboxes={false}
        parentsAsValues
        keepParentOpen
        onSelectionChanged={(selectedItems: string[]) => {
          setSelectedProviderNetworkTypes(
            selectedItems.map(item => item as ProviderNetworkTypeEnum)
          );
        }}
        selectedItems={selectedProviderNetworkTypes}
        testId="provider-hub__filter-provider-source-multi-select"
      />

      <MultiselectDropdown
        containerClassName="filter-item"
        customSelectInputText={statusSelectionText}
        items={providerHubContext.providerActionStatusOptions}
        label="Provider Action Status"
        onSelectionChanged={(selectedItems: DropdownItem<ProviderActionStatusEnum | null>[]) =>
          setSelectedProviderActionStatuses(selectedItems.map(item => item.key))
        }
        selectedItems={selectedProviderActionStatuses.map(s => ({
          key: s as ProviderActionStatusEnum,
          name: ProviderActionStatusEnumToDisplay(s),
        }))}
        testId="provider-hub__filter-status-multi-select"
        showTooltipOnOverflow={true}
        validationTooltipContent={
          <div className="provider-hub__filter_definitions-tooltip">
            <p className='text-center'><strong>Provider Action Status Definitions:</strong></p>
            <ul>
              <li><strong>No Actions Applied:</strong> A servicing provider who does not have an action applied to any of their access points.</li>
              <li><strong>Partially Targeted, Terminated, or Excluded:</strong> A servicing provider who has had an action applied at one or more—but not all—of their applicable access points.</li>
              <li><strong>Targeted, Terminated, or Excluded:</strong> A servicing provider who has had an action applied across all of their applicable access points.</li>
            </ul>
          </div>
        }
      />

      <div className="filter-item">
        <DualRangeSlider
          label="Gap Closure Potential"
          minValue={providerHubContext.defaultCloseGapsRangeMin}
          maxValue={providerHubContext.defaultCloseGapsRangeMax}
          value={[selectedCloseGapsMin, selectedCloseGapsMax]}
          onChange={selectedItems => {
            setSelectedCloseGapsMin(selectedItems[0]);
            setSelectedCloseGapsMax(selectedItems[1]);
          }}
          testId="provider-hub__filter-gap-impact-dual-range-slider"
        />
      </div>

      {providerHubContext.filterState.searchByOption !== ProviderSearchByEnum.ContractEntity &&
        providerHubContext.filterState.viewByOption !== ProviderViewByEnum.Organization &&
        providerHubContext.filterState.viewByOption !== ProviderViewByEnum.Hospital &&
        <div className="filter-item">
          <DualRangeSlider
            label="Number of Provider Access Points "
            minValue={providerHubContext.defaultAccessPointsRangeMin}
            maxValue={providerHubContext.defaultAccessPointsRangeMax}
            value={[selectedAccessPointCountMin, selectedAccessPointCountMax]}
            onChange={selectedItems => {
              setSelectedAccessPointCountMin(selectedItems[0]);
              setSelectedAccessPointCountMax(selectedItems[1]);
            }}
            testId="provider-hub__filter-number-of-access-points-dual-range-slider"
          />
        </div>
      }

      {(providerHubContext.filterState.searchByOption === ProviderSearchByEnum.ContractEntity ||
        providerHubContext.filterState.viewByOption === ProviderViewByEnum.Organization ||
        providerHubContext.filterState.viewByOption === ProviderViewByEnum.Hospital
      ) &&
        <div className="filter-item">
          <DualRangeSlider
            label={<>Servicing Providers <InfoIndicator tooltipTitle="Number of Servicing Providers associated with the Contract Entity" /></>}
            minValue={providerHubContext.defaultServicingProviderCountMin}
            maxValue={providerHubContext.defaultServicingProviderCountMax}
            value={[selectedServicingProviderCountMin, selectedServicingProviderCountMax]}
            onChange={selectedItems => {
              setSelectedServicingProviderCountMin(selectedItems[0]);
              setSelectedServicingProviderCountMax(selectedItems[1]);
            }}
            testId="provider-hub__filter-number-of-servicing-provider-dual-range-slider"
          />
        </div>
      }

      {(providerHubContext.showPciFilters || (!isAggregateView && providerHubContext.showNetworkIntegrityFilters)) && (
        <div className="horizontal-divider" />
      )}
      {providerHubContext.showPciFilters && (
        <Collapsible
          title="PROVIDER CLAIMS INSIGHTS FILTERS"
          testId="provider-hub-advanced-filters"
        >
          <>
            {!isAggregateView && ffContext.enabledFeatureFlags.has(Features.ENABLE_PROVIDER_HUB_PCI_FILTER_SEARCH) && (
              <>
                <TypeaheadMultiselectDropdown
                  containerClassName="filter-item d-block"
                  customSelectInputText={topProceduresSelectionText}
                  descriptiveText={providerHubContext.errorLoadingTopProcedures ? "Failed to load Top Procedures, please refresh to try again." : undefined}
                  descriptiveTextClassname="text-danger"
                  disabled={providerHubContext.errorLoadingTopProcedures}
                  items={providerHubContext.topProcedureOptions}
                  isLoading={providerHubContext.isLoadingTopProcedures}
                  label="Top Procedures Search"
                  onSelectionChanged={selectedItems =>
                    setSelectedTopProcedures(
                      selectedItems.map(item => ({
                        ...item,
                        key: item.key as string
                      }))
                    )
                  }
                  selectedItems={selectedTopProcedures}
                  showTooltipOnOverflow={true}
                  testId="provider-hub__filter-top-procedures"
                  validationTooltipContent={"Top 5 procedure codes for an NPI."}
                />

                <TypeaheadMultiselectDropdown
                  containerClassName="filter-item d-block"
                  customSelectInputText={topConditionsSelectionText}
                  descriptiveText={providerHubContext.errorLoadingTopConditions ? "Failed to load Top Conditions, please refresh to try again." : undefined}
                  descriptiveTextClassname="text-danger"
                  disabled={providerHubContext.errorLoadingTopConditions}
                  items={providerHubContext.topConditionOptions}
                  isLoading={providerHubContext.isLoadingTopConditions}
                  label="Top Conditions Search"
                  onSelectionChanged={selectedItems =>
                    setSelectedTopConditions(
                      selectedItems.map(item => ({
                        ...item,
                        key: item.key as string
                      }))
                    )
                  }
                  selectedItems={selectedTopConditions}
                  showTooltipOnOverflow={true}
                  testId="provider-hub__filter-top-conditions"
                  validationTooltipContent={"Top 5 condition codes for an NPI."}
                />

                <TypeaheadMultiselectDropdown
                  containerClassName="filter-item d-block"
                  customSelectInputText={topPrescriptionsSelectionText}
                  descriptiveText={topPrescriptionsErrorMessage}
                  descriptiveTextClassname="text-danger"
                  disabled={providerHubContext.errorLoadingTopPrescriptions}
                  items={providerHubContext.topPrescriptionOptions}
                  isLoading={providerHubContext.isLoadingTopPrescriptions}
                  label="Top Prescriptions Search"
                  onSelectionChanged={selectedItems =>
                    setSelectedTopPrescriptions(
                      selectedItems.map(item => ({
                        ...item,
                        key: item.key as string
                      }))
                    )
                  }
                  selectedItems={selectedTopPrescriptions}
                  showTooltipOnOverflow={true}
                  testId="provider-hub__filter-top-prescriptions"
                  validationTooltipContent="Top 5 prescriptions for an NPI."
                />
              </>
            )}

            <div className="filter-item">
              {isAggregateView ? (
                <>
                  <DualRangeSlider
                    label={
                      <span>
                        Effectiveness (Quality) Score
                        <TooltipWrapper
                          text={
                            <div className="provider-hub__filter_definitions-tooltip">
                              <p>Quality (or effectiveness) is a measure of quality where 5 is the best score (higher quality) and 1 is the worst score (lower quality). Scores are relative to other providers in the same specialty and state and are derived from claims insights into:</p>
                              <ol>
                                <li>Mortality</li>
                                <li>Complications</li>
                                <li>Days in acute care</li>
                              </ol>
                            </div>
                          }
                          position="top"
                          containerClassName="ms-1"
                        >
                          <QesIcon
                            iconName={QesIcons.InfoCircle}
                            description="focus or hover for a tooltip"
                          />
                        </TooltipWrapper>
                      </span>
                    }
                    testId="provider-hub__filter-pci-effectiveness-dual-range-slider"
                    minValue={providerHubContext.defaultEffectivenessRangeMin}
                    maxValue={providerHubContext.defaultEffectivenessRangeMax}
                    value={[selectedEffectivenessMin, selectedEffectivenessMax]}
                    onChange={selectedItems => {
                      setSelectedEffectivenessMin(selectedItems[0]);
                      setSelectedEffectivenessMax(selectedItems[1]);
                    }}
                    step={0.1}
                  />
                  <QESCheckbox
                    checked={includeUnknownEffectiveness}
                    label="Include Unknown"
                    onChange={() => setIncludeUnknownEffectiveness(prev => !prev)}
                    testId="provider-hub__filter-pci-effectiveness-include-unknown-checkbox"
                  />
                </>
              ) : (
                <RoundCheckbox
                  includesUnknown={true}
                  label={
                    <span>
                      Effectiveness (Quality) Score
                      <TooltipWrapper
                        text={
                          <div className="provider-hub__filter_definitions-tooltip">
                            <p>Quality (or effectiveness) is a measure of quality where 5 is the best score (higher quality) and 1 is the worst score (lower quality). Scores are relative to other providers in the same specialty and state and are derived from claims insights into:</p>
                            <ol>
                              <li>Mortality</li>
                              <li>Complications</li>
                              <li>Days in acute care</li>
                            </ol>
                          </div>
                        }
                        position="top"
                        containerClassName="ms-1"
                      >
                        <QesIcon
                          iconName={QesIcons.InfoCircle}
                          description="focus or hover for a tooltip"
                        />
                      </TooltipWrapper>
                    </span>
                  }
                  values={effectivenessEfficiencyValues}
                  testId="provider-hub__filter-pci-effectiveness-checkbox"
                  isChecked={getEffectivenessChecked}
                  onChange={handleEffectivenessClick}
                />
              )}
            </div>

            <div className="filter-item">
              {isAggregateView ? (
                <>
                  <DualRangeSlider
                    label={
                      <span>
                        Efficiency (Cost) Score
                        <TooltipWrapper
                          text={
                            <div className="provider-hub__filter_definitions-tooltip">
                              <p>Cost (or Efficiency) is a measure of a physician cost vs. peers where 5 is the best score (lower cost) and 1 is the worst score (higher cost). Efficiency measures are based on:</p>
                              <ol>
                                <li>Cost per visit</li>
                                <li>Cost per patient</li>
                              </ol>
                              <p>Standardized pricing methodology is used, meaning efficiency is a measure of comparative utilization and not rate/price differences.</p>
                            </div>
                          }
                          position="top"
                          containerClassName="ms-1"
                        >
                          <QesIcon
                            iconName={QesIcons.InfoCircle}
                            description="focus or hover for a tooltip"
                          />
                        </TooltipWrapper>
                      </span>
                    }
                    testId="provider-hub__filter-pci-efficiency-dual-range-slider"
                    minValue={providerHubContext.defaultEfficiencyRangeMin}
                    maxValue={providerHubContext.defaultEfficiencyRangeMax}
                    value={[selectedEfficiencyMin, selectedEfficiencyMax]}
                    onChange={selectedItems => {
                      setSelectedEfficiencyMin(selectedItems[0]);
                      setSelectedEfficiencyMax(selectedItems[1]);
                    }}
                    step={0.1}
                  />
                  <QESCheckbox
                    checked={includeUnknownEfficiency}
                    label="Include Unknown"
                    onChange={() => setIncludeUnknownEfficiency(prev => !prev)}
                    testId="provider-hub__filter-pci-efficiency-include-unknown-checkbox"
                  />
                </>
              ) : (
                <RoundCheckbox
                  includesUnknown={true}
                  label={
                    <span>
                      Efficiency (Cost) Score
                      <TooltipWrapper
                        text={
                          <div className="provider-hub__filter_definitions-tooltip">
                            <p>Cost (or Efficiency) is a measure of a physician cost vs. peers where 5 is the best score (lower cost) and 1 is the worst score (higher cost). Efficiency measures are based on:</p>
                            <ol>
                              <li>Cost per visit</li>
                              <li>Cost per patient</li>
                            </ol>
                            <p>Standardized pricing methodology is used, meaning efficiency is a measure of comparative utilization and not rate/price differences.</p>
                          </div>
                        }
                        position="top"
                        containerClassName="ms-1"
                      >
                        <QesIcon
                          iconName={QesIcons.InfoCircle}
                          description="focus or hover for a tooltip"
                        />
                      </TooltipWrapper>
                    </span>
                  }
                  values={effectivenessEfficiencyValues}
                  testId="provider-hub__filter-pci-efficiency-checkbox"
                  isChecked={getEfficiencyChecked}
                  onChange={handleEfficiencyClick}
                />
              )}
            </div>

            {!isAggregateView && providerHubContext.showLocationConfidenceFilter && (
              <MultiselectDropdown
                containerClassName="filter-item"
                customSelectInputText={locationConfidenceSelectionText}
                items={providerHubContext.locationConfidenceOptions}
                label="Location Confidence"
                onSelectionChanged={selectedItems =>
                  setSelectedLocationConfidence(
                    selectedItems.map(item => ({
                      ...item,
                      key: item.key as LocationConfidenceScoresLabelEnum
                    }))
                  )
                }
                selectedItems={selectedLocationConfidence}
                testId="provider-hub__filter-location-confidence-multi-select"
                showTooltipOnOverflow={true}
              />
            )}
            {!isAggregateView && (
              <>
                <MultiselectDropdown
                  containerClassName="filter-item"
                  customSelectInputText={totalClaimsVolumeSelectionText}
                  items={providerHubContext.pciVolumeOptions}
                  label="Total Claims Volume"
                  onSelectionChanged={selectedItems =>
                    setSelectedTotalClaimsVolume(
                      selectedItems.map(item => ({
                        ...item,
                        key: item.key as ClaimsActivityVolumeLabelEnum
                      }))
                    )
                  }
                  selectedItems={selectedTotalClaimsVolume}
                  testId="provider-hub__filter-total-claims-volume-multi-select"
                  showTooltipOnOverflow={true}
                />

                <MultiselectDropdown
                  containerClassName="filter-item"
                  customSelectInputText={totalNewPatientIndicatorSelectionText}
                  items={providerHubContext.pciClaimsOptions}
                  label="Total New Patient Claims"
                  onSelectionChanged={selectedItems =>
                    setSelectedTotalNewPatientIndicator(
                      selectedItems.map(item => ({
                        ...item,
                        key: item.key as boolean | null
                      }))
                    )
                  }
                  selectedItems={selectedTotalNewPatientIndicator}
                  testId="provider-hub__filter-total-new-patient-indicator-multi-select"
                  showTooltipOnOverflow={true}
                />

                <MultiselectDropdown
                  containerClassName="filter-item"
                  customSelectInputText={commercialClaimsVolumeSelectionText}
                  items={providerHubContext.pciVolumeOptions}
                  label="Commercial Claims Volume"
                  onSelectionChanged={selectedItems =>
                    setSelectedCommercialClaimsVolume(
                      selectedItems.map(item => ({
                        ...item,
                        key: item.key as ClaimsActivityVolumeLabelEnum
                      }))
                    )
                  }
                  selectedItems={selectedCommercialClaimsVolume}
                  testId="provider-hub__filter-commercial-claims-volume-multi-select"
                  showTooltipOnOverflow={true}
                />

                <MultiselectDropdown
                  containerClassName="filter-item"
                  customSelectInputText={commercialNewPatientIndicatorSelectionText}
                  items={providerHubContext.pciClaimsOptions}
                  label="Commercial New Patient Claims"
                  onSelectionChanged={selectedItems =>
                    setSelectedCommercialNewPatientIndicator(
                      selectedItems.map(item => ({
                        ...item,
                        key: item.key as boolean | null
                      }))
                    )
                  }
                  selectedItems={selectedCommercialNewPatientIndicator}
                  testId="provider-hub__filter-commercial-new-patient-indicator-multi-select"
                  showTooltipOnOverflow={true}
                />

                <MultiselectDropdown
                  containerClassName="filter-item"
                  customSelectInputText={medicaidClaimsVolumeSelectionText}
                  items={providerHubContext.pciVolumeOptions}
                  label="Medicaid Claims Volume"
                  onSelectionChanged={selectedItems =>
                    setSelectedMedicaidClaimsVolume(
                      selectedItems.map(item => ({
                        ...item,
                        key: item.key as ClaimsActivityVolumeLabelEnum
                      }))
                    )
                  }
                  selectedItems={selectedMedicaidClaimsVolume}
                  testId="provider-hub__filter-medicaid-claims-volume-multi-select"
                  showTooltipOnOverflow={true}
                />

                <MultiselectDropdown
                  containerClassName="filter-item"
                  customSelectInputText={medicaidNewPatientIndicatorSelectionText}
                  items={providerHubContext.pciClaimsOptions}
                  label="Medicaid New Patient Claims"
                  onSelectionChanged={selectedItems =>
                    setSelectedMedicaidNewPatientIndicator(
                      selectedItems.map(item => ({
                        ...item,
                        key: item.key as boolean | null
                      }))
                    )
                  }
                  selectedItems={selectedMedicaidNewPatientIndicator}
                  testId="provider-hub__filter-medicaid-new-patient-indicator-multi-select"
                  showTooltipOnOverflow={true}
                />

                <MultiselectDropdown
                  containerClassName="filter-item"
                  customSelectInputText={medicareClaimsVolumeSelectionText}
                  items={providerHubContext.pciVolumeOptions}
                  label="Medicare Claims Volume"
                  onSelectionChanged={selectedItems =>
                    setSelectedMedicareClaimsVolume(
                      selectedItems.map(item => ({
                        ...item,
                        key: item.key as ClaimsActivityVolumeLabelEnum
                      }))
                    )
                  }
                  selectedItems={selectedMedicareClaimsVolume}
                  testId="provider-hub__filter-medicare-claims-volume-multi-select"
                  showTooltipOnOverflow={true}
                />

                <MultiselectDropdown
                  containerClassName="filter-item"
                  customSelectInputText={medicareNewPatientIndicatorSelectionText}
                  items={providerHubContext.pciClaimsOptions}
                  label="Medicare New Patient Claims"
                  onSelectionChanged={selectedItems =>
                    setSelectedMedicareNewPatientIndicator(
                      selectedItems.map(item => ({
                        ...item,
                        key: item.key as boolean | null
                      }))
                    )
                  }
                  selectedItems={selectedMedicareNewPatientIndicator}
                  testId="provider-hub__filter-medicare-new-patient-indicator-multi-select"
                  showTooltipOnOverflow={true}
                />
              </>
            )}
            {!isAggregateView && providerHubContext.showTriCareFilters && (
              <>
                <MultiselectDropdown
                  containerClassName="filter-item"
                  customSelectInputText={tricareClaimsVolumeSelectionText}
                  items={providerHubContext.pciVolumeOptions}
                  label="TRICARE Claims Volume"
                  onSelectionChanged={selectedItems =>
                    setSelectedTricareClaimsVolume(
                      selectedItems.map(item => ({
                        ...item,
                        key: item.key as ClaimsActivityVolumeLabelEnum
                      }))
                    )
                  }
                  selectedItems={selectedTricareClaimsVolume}
                  testId="provider-hub__filter-tricare-claims-volume-multi-select"
                  showTooltipOnOverflow={true}
                />

                <MultiselectDropdown
                  containerClassName="filter-item"
                  customSelectInputText={tricareNewPatientIndicatorSelectionText}
                  items={providerHubContext.pciClaimsOptions}
                  label="TRICARE New Patient Claims"
                  onSelectionChanged={selectedItems =>
                    setSelectedTricareNewPatientIndicator(
                      selectedItems.map(item => ({
                        ...item,
                        key: item.key as boolean | null
                      }))
                    )
                  }
                  selectedItems={selectedTricareNewPatientIndicator}
                  testId="provider-hub__filter-tricare-new-patient-indicator-multi-select"
                  showTooltipOnOverflow={true}
                />
              </>
            )}

            {!isAggregateView && providerHubContext.showVaFilters && (
              <>
                <MultiselectDropdown
                  containerClassName="filter-item"
                  customSelectInputText={vaClaimsVolumeSelectionText}
                  items={providerHubContext.pciVolumeOptions}
                  label="VA Claims Volume"
                  onSelectionChanged={selectedItems =>
                    setSelectedVaClaimsVolume(
                      selectedItems.map(item => ({
                        ...item,
                        key: item.key as ClaimsActivityVolumeLabelEnum
                      }))
                    )
                  }
                  selectedItems={selectedVaClaimsVolume}
                  testId="provider-hub__filter-va-claims-volume-multi-select"
                  showTooltipOnOverflow={true}
                />
                <MultiselectDropdown
                  containerClassName="filter-item"
                  customSelectInputText={vaNewPatientIndicatorSelectionText}
                  items={providerHubContext.pciClaimsOptions}
                  label="VA New Patient Claims"
                  onSelectionChanged={selectedItems =>
                    setSelectedVaNewPatientIndicator(
                      selectedItems.map(item => ({
                        ...item,
                        key: item.key as boolean | null
                      }))
                    )
                  }
                  selectedItems={selectedVaNewPatientIndicator}
                  testId="provider-hub__filter-va-new-patient-indicator-multi-select"
                  showTooltipOnOverflow={true}
                />
              </>
            )}
            {providerHubContext.showMotiveScoreFilters && (
              <>
                <div className="horizontal-divider" />
                <h1 className="provider-hub-advanced-filters-header">
                  MOTIVE APPROPRIATENESS
                </h1>
              </>
            )}
            {providerHubContext.showMotiveScoreFilters &&
              !isAggregateView && (
                <div className="filter-item">
                  <RoundCheckbox
                    includesUnknown={true}
                    label="Motive Appropriateness Tier"
                    values={motiveScoreValues}
                    testId="provider-hub__filter-motive-score-checkbox"
                    isChecked={getMotiveScoreChecked}
                    onChange={handleMotiveScoreClick}
                  />
                </div>
            )}

            {providerHubContext.showMotiveScoreFilters &&
              isAggregateView && (
                <div className="filter-item">
                  <DualRangeSlider
                    label={
                      <span>
                        Motive Appropriateness Tier
                      </span>
                    }
                    testId="provider-hub__filter-pci-motive-score-dual-range-slider"
                    minValue={providerHubContext.defaultMotiveScoreRangeMin}
                    maxValue={providerHubContext.defaultMotiveScoreRangeMax}
                    value={[selectedMotiveScoreMin, selectedMotiveScoreMax]}
                    onChange={selectedItems => {
                      setSelectedMotiveScoreMin(selectedItems[0]);
                      setSelectedMotiveScoreMax(selectedItems[1]);
                    }}
                    step={0.1}
                  />
                  <QESCheckbox
                    checked={includeUnknownMotiveScore}
                    label="Include Unknown"
                    onChange={() => setIncludeUnknownMotiveScore(prev => !prev)}
                    testId="provider-hub__filter-pci-motive-score-include-unknown-checkbox"
                  />
                </div>
            )}

            {providerHubContext.showCareJourneyFilters && (
              <>
                <div className="horizontal-divider" />
                <h1 className="provider-hub-advanced-filters-header">
                  CAREJOURNEY PROVIDER PERFORMANCE
                </h1>

                <div className="filter-item">
                  {isAggregateView ? (
                    <>
                      <DualRangeSlider
                        label={
                          <span>
                            CareJourney Cost
                            <TooltipWrapper
                              text={getCareJourneyDefinitions()}
                              position="top"
                              containerClassName="ms-1"
                            >
                              <QesIcon
                                iconName={QesIcons.InfoCircle}
                                description="focus or hover for a tooltip"
                              />
                            </TooltipWrapper>
                          </span>
                        }
                        testId="provider-hub__filter-carejourney-cost-score-dual-range-slider"
                        minValue={providerHubContext.defaultCareJourneyCostRangeMin}
                        maxValue={providerHubContext.defaultCareJourneyCostRangeMax}
                        value={[selectedCareJourneyCostScoreMin, selectedCareJourneyCostScoreMax]}
                        onChange={selectedItems => {
                          setSelectedCareJourneyCostScoreMin(selectedItems[0]);
                          setSelectedCareJourneyCostScoreMax(selectedItems[1]);
                        }}
                        step={0.1}
                      />
                      <QESCheckbox
                        checked={includeUnknownCareJourneyCostScore}
                        label="Include Unknown"
                        onChange={() => setIncludeUnknownCareJourneyCostScore(prev => !prev)}
                        testId="provider-hub__filter-carejourney-cost-score-include-unknown-checkbox"
                      />
                    </>
                  ) : (
                    <RoundCheckbox
                      includesUnknown={true}
                      label={
                        <span>
                          CareJourney Cost
                          <TooltipWrapper
                            text={getCareJourneyDefinitions()}
                            position="top"
                            containerClassName="ms-1"
                          >
                            <QesIcon
                              iconName={QesIcons.InfoCircle}
                              description="focus or hover for a tooltip"
                            />
                          </TooltipWrapper>
                        </span>
                      }
                      values={effectivenessEfficiencyValues}
                      testId="provider-hub__filter-carejourney-cost-score-checkbox"
                      isChecked={getCareJourneyCostScoreChecked}
                      onChange={handleCareJourneyCostScoreClick}
                    />
                  )}
                </div>

                <div className="filter-item">
                  {isAggregateView ? (
                    <>
                      <DualRangeSlider
                        label={
                          <span>
                            CareJourney Quality
                            <TooltipWrapper
                              text={getCareJourneyDefinitions()}
                              position="top"
                              containerClassName="ms-1"
                            >
                              <QesIcon
                                iconName={QesIcons.InfoCircle}
                                description="focus or hover for a tooltip"
                              />
                            </TooltipWrapper>
                          </span>
                        }
                        testId="provider-hub__filter-carejourney-quality-score-dual-range-slider"
                        minValue={providerHubContext.defaultCareJourneyQualityRangeMin}
                        maxValue={providerHubContext.defaultCareJourneyQualityRangeMax}
                        value={[selectedCareJourneyQualityScoreMin, selectedCareJourneyQualityScoreMax]}
                        onChange={selectedItems => {
                          setSelectedCareJourneyQualityScoreMin(selectedItems[0]);
                          setSelectedCareJourneyQualityScoreMax(selectedItems[1]);
                        }}
                        step={0.1}
                      />
                      <QESCheckbox
                        checked={includeUnknownCareJourneyQualityScore}
                        label="Include Unknown"
                        onChange={() => setIncludeUnknownCareJourneyQualityScore(prev => !prev)}
                        testId="provider-hub__filter-carejourney-quality-score-include-unknown-checkbox"
                      />
                    </>
                  ) : (
                    <RoundCheckbox
                      includesUnknown={true}
                      label={
                        <span>
                          CareJourney Quality
                          <TooltipWrapper
                            text={getCareJourneyDefinitions()}
                            position="top"
                            containerClassName="ms-1"
                          >
                            <QesIcon
                              iconName={QesIcons.InfoCircle}
                              description="focus or hover for a tooltip"
                            />
                          </TooltipWrapper>
                        </span>
                      }
                      values={effectivenessEfficiencyValues}
                      testId="provider-hub__filter-carejourney-quality-score-checkbox"
                      isChecked={getCareJourneyQualityScoreChecked}
                      onChange={handleCareJourneyQualityScoreClick}
                    />
                  )}
                </div>
              </>
            )}
          </>
        </Collapsible>
      )}

      {!isAggregateView && providerHubContext.showNetworkIntegrityFilters && (
        <Collapsible
          title="NETWORK INTEGRITY FILTERS"
          testId="network-integrity-advanced-filters"
        >
          <>
            <div className="filter-item">
              <span className="round-checkbox-label">Project Options Exclusions</span>
              <div className="filter-item-column">
                <QESCheckbox
                  checked={allChecked(niProjectOptionsExclusionsValues, selectedNiProjectOptionsExclusions)}
                  indeterminate={
                    someChecked(niProjectOptionsExclusionsValues, selectedNiProjectOptionsExclusions) &&
                    !allChecked(niProjectOptionsExclusionsValues, selectedNiProjectOptionsExclusions)
                  }
                  label="Hide All"
                  testId="provider-hub__filter-ni-project-options-exclusions-hide-all"
                  onChange={(e) =>
                    e.target.checked ? setSelectedNiProjectOptionsExclusions([...niProjectOptionsExclusionsValues])
                      : setSelectedNiProjectOptionsExclusions([])
                  }
                />
                {niProjectOptionsExclusionsValues.map(value => (
                  <QESCheckbox
                    key={value}
                    checked={selectedNiProjectOptionsExclusions.includes(value)}
                    label={niHideLabels[value]}
                    testId={`provider-hub__filter-ni-project-options-exclusions-${value}`}
                    onChange={() =>
                      setSelectedNiProjectOptionsExclusions(prev =>
                        prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
                      )
                    }
                  />
                ))}
              </div>
            </div>

            {providerHubContext.showNetworkIntegrityCriticalAlertsFilter && (
              <div className="filter-item">
                <span className="round-checkbox-label">Critical Alerts</span>
                <div className="filter-item-column">
                  <QESCheckbox
                    checked={allChecked(niCriticalAlertsValues, selectedNiCriticalAlerts)}
                    indeterminate={
                      someChecked(niCriticalAlertsValues, selectedNiCriticalAlerts) &&
                      !allChecked(niCriticalAlertsValues, selectedNiCriticalAlerts)
                    }
                    label="Hide All"
                    testId="provider-hub__filter-ni-critical-alerts-hide-all"
                    onChange={(e) =>
                      e.target.checked ? setSelectedNiCriticalAlerts([...niCriticalAlertsValues])
                        : setSelectedNiCriticalAlerts([])
                    }
                  />
                  {niCriticalAlertsValues.map(value => (
                    <QESCheckbox
                      key={value}
                      checked={selectedNiCriticalAlerts.includes(value)}
                      label={niHideLabels[value]}
                      testId={`provider-hub__filter-ni-critical-alerts-${value}`}
                      onChange={() =>
                        setSelectedNiCriticalAlerts(prev =>
                          prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
                        )
                      }
                    />
                  ))}
                </div>
              </div>
            )}

            {providerHubContext.showNetworkIntegrityAttestationBasedAlertsFilter && (
              <div className="filter-item">
                <span className="round-checkbox-label">Attestation-based Alerts</span>
                <div className="filter-item-column">
                  <QESCheckbox
                    checked={allChecked(niAttestationBasedAlertsValues, selectedNiAttestationBasedAlerts)}
                    indeterminate={
                      someChecked(niAttestationBasedAlertsValues, selectedNiAttestationBasedAlerts) &&
                      !allChecked(niAttestationBasedAlertsValues, selectedNiAttestationBasedAlerts)
                    }
                    label="Hide All"
                    testId="provider-hub__filter-ni-attestation-based-alerts-hide-all"
                    onChange={(e) =>
                      e.target.checked ? setSelectedNiAttestationBasedAlerts([...niAttestationBasedAlertsValues])
                        : setSelectedNiAttestationBasedAlerts([])
                    }
                  />
                  {niAttestationBasedAlertsValues.map(value => (
                    <QESCheckbox
                      key={value}
                      checked={selectedNiAttestationBasedAlerts.includes(value)}
                      label={niHideLabels[value]}
                      testId={`provider-hub__filter-ni-attestation-based-alerts-${value}`}
                      onChange={() =>
                        setSelectedNiAttestationBasedAlerts(prev =>
                          prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
                        )
                      }
                    />
                  ))}
                </div>
              </div>
            )}

            <div className="filter-item">
              <DualRangeSlider
                label="Number of Addresses"
                minValue={providerHubContext.defaultExcessAddressesRangeMin}
                maxValue={providerHubContext.defaultExcessAddressesRangeMax}
                maxAsPlus={true}
                value={[selectedExcessAddressesMin, selectedExcessAddressesMax]}
                onChange={selectedItems => {
                  setSelectedExcessAddressesMin(selectedItems[0]);
                  setSelectedExcessAddressesMax(selectedItems[1]);
                }}
                testId="provider-hub__filter-ni-excess-addresses-dual-range-slider"
              />
            </div>

            <div className="filter-item">
              <DualRangeSlider
                label="Number of Specialties"
                minValue={providerHubContext.defaultExcessSpecialtiesRangeMin}
                maxValue={providerHubContext.defaultExcessSpecialtiesRangeMax}
                maxAsPlus={true}
                value={[selectedExcessSpecialtiesMin, selectedExcessSpecialtiesMax]}
                onChange={selectedItems => {
                  setSelectedExcessSpecialtiesMin(selectedItems[0]);
                  setSelectedExcessSpecialtiesMax(selectedItems[1]);
                }}
                testId="provider-hub__filter-ni-excess-specialties-dual-range-slider"
              />
            </div>
          </>
        </Collapsible>
      )}

      <FlyoutFooter
        submitButton={
          <QESButton
            disabled={!areFiltersValid}
            onClick={handleApply}
            qesType="primary"
            qesTestId="provider-hub__filter-apply-button">
            Apply
          </QESButton>
        }
        onClose={handleCancel}
      />
    </div>
  );
}

export default ProviderHubFilters;
